import React, { useState } from 'react';
import { Users, Calendar, FileText, BookOpen, GraduationCap, CheckCircle } from 'lucide-react';

const AdmissionsPage = () => {
  const [activeProgram, setActiveProgram] = useState('nd');
  const [activeSchool, setActiveSchool] = useState('all');

  // ND Programs data organized by school
  const ndPrograms = {
    all: [
      { school: "SCHOOL OF AGRICULTURE AND AGRICULTURAL TECHNOLOGY", programs: ["Agricultural Technology", "Horticulture and Landscape Technology"] },
      { school: "SCHOOL OF APPLIED AND NATURAL SCIENCES", programs: ["Tourism Management Technology", "Hospitality Management", "Health Information Technology", "Food Science Technology", "Nutrition and Dietetics", "Science Laboratory Technology", "Statistics"] },
      { school: "SCHOOL OF BUSINESS AND MANAGEMENT", programs: ["Business Administration and Management", "Marketing", "Public Administration", "Procurement and Supply Chain"] },
      { school: "SCHOOL OF ENGINEERING TECHNOLOGY", programs: ["Agricultural and Bio-Environmental Engineering Technology", "Chemical Engineering", "Civil Engineering", "Computer Engineering", "Electrical Engineering", "Mechanical Engineering", "Renewable Energy"] },
      { school: "SCHOOL OF ENVIRONMENTAL STUDIES", programs: ["Architectural Technology", "Building Technology", "Estate Management and Valuation", "Environmental Science and Management", "Quantity Surveying", "Surveying and Geo-Informatics", "Urban and Regional Planning"] },
      { school: "SCHOOL OF FINANCIAL STUDIES", programs: ["Accountancy", "Banking and Finance", "Taxation"] },
      { school: "SCHOOL OF INFORMATION AND COMMUNICATION TECHNOLOGY", programs: ["Computer Science", "Library and Information Science", "Mass Communication", "Office Technology and Management"] }
    ],
    agriculture: [
      { school: "SCHOOL OF AGRICULTURE AND AGRICULTURAL TECHNOLOGY", programs: ["Agricultural Technology", "Horticulture and Landscape Technology"] }
    ],
    appliedSciences: [
      { school: "SCHOOL OF APPLIED AND NATURAL SCIENCES", programs: ["Tourism Management Technology", "Hospitality Management", "Health Information Technology", "Food Science Technology", "Nutrition and Dietetics", "Science Laboratory Technology", "Statistics"] }
    ],
    business: [
      { school: "SCHOOL OF BUSINESS AND MANAGEMENT", programs: ["Business Administration and Management", "Marketing", "Public Administration", "Procurement and Supply Chain"] }
    ],
    engineering: [
      { school: "SCHOOL OF ENGINEERING TECHNOLOGY", programs: ["Agricultural and Bio-Environmental Engineering Technology", "Chemical Engineering", "Civil Engineering", "Computer Engineering", "Electrical Engineering", "Mechanical Engineering", "Renewable Energy"] }
    ],
    environmental: [
      { school: "SCHOOL OF ENVIRONMENTAL STUDIES", programs: ["Architectural Technology", "Building Technology", "Estate Management and Valuation", "Environmental Science and Management", "Quantity Surveying", "Surveying and Geo-Informatics", "Urban and Regional Planning"] }
    ],
    financial: [
      { school: "SCHOOL OF FINANCIAL STUDIES", programs: ["Accountancy", "Banking and Finance", "Taxation"] }
    ],
    ict: [
      { school: "SCHOOL OF INFORMATION AND COMMUNICATION TECHNOLOGY", programs: ["Computer Science", "Library and Information Science", "Mass Communication", "Office Technology and Management"] }
    ]
  };

  // HND Programs data
  const hndPrograms = {
    all: [
      { school: "SCHOOL OF AGRICULTURE AND AGRICULTURAL SCIENCES", programs: [
        "Agricultural Technology (Crop Production)",
        "Horticulture and Landscape Technology"
      ]},
      { school: "SCHOOL OF APPLIED AND NATURAL SCIENCES", programs: [
        "Biology", "Biology/Microbiology", "Environmental Biology", "Microbiology",
        "Chemistry", "Biochemistry", "Biochemistry/Microbiology", "Chemistry/Biochemistry",
        "Physics/Electronics", "Hospitality Management", "Statistics", "Nutrition and Dietetics",
        "Tourism & Management Technology"
      ]},
      { school: "SCHOOL OF BUSINESS ADMINISTRATION AND MANAGEMENT", programs: [
        "Business Administration and Management", "Marketing", "Public Administration"
      ]},
      { school: "SCHOOL OF ENGINEERING TECHNOLOGY", programs: [
        "Agricultural and Bio-Environmental Engineering Technology (Farm Power and Machinery, Post-Harvest Technology, Soil and Water Engineering)",
        "Chemical Engineering", "Civil Engineering", "Computer Engineering",
        "Electrical Engineering Technology (Electronics and Telecommunication, Power and Machines)",
        "Mechanical Engineering (Plant Engineering, Manufacturing Engineering, Automotive)"
      ]},
      { school: "SCHOOL OF ENVIRONMENTAL STUDIES", programs: [
        "Architectural Technology", "Building Technology", "Estate Management and Valuation",
        "Quantity Surveying", "Surveying and Geo-Informatics", "Urban and Regional Planning"
      ]},
      { school: "SCHOOL OF FINANCIAL STUDIES", programs: [
        "Accountancy", "Banking and Finance"
      ]},
      { school: "SCHOOL OF INFORMATION AND COMMUNICATION TECHNOLOGY", programs: [
        "Computer Science (Software and Web Development, Networking & Clouds Computing, Artificial Intelligence, Cyber Security)",
        "Mass Communication (Journalism and Media Studies)", "Office Technology and Management",
        "Library and Information Science"
      ]}
    ]
  };

  // School filter options
  const schoolFilters = [
    { id: 'all', name: 'All Schools' },
    { id: 'agriculture', name: 'Agriculture' },
    { id: 'appliedSciences', name: 'Applied Sciences' },
    { id: 'business', name: 'Business' },
    { id: 'engineering', name: 'Engineering' },
    { id: 'environmental', name: 'Environmental Studies' },
    { id: 'financial', name: 'Financial Studies' },
    { id: 'ict', name: 'ICT' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Admissions</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join our community of innovators and leaders. Applications for 2026/2027 academic session are now open.
          </p>
        </div>

        {/* Program Type Selection */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setActiveProgram('nd')}
              className={`px-6 py-3 text-sm font-medium rounded-l-lg ${
                activeProgram === 'nd' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <GraduationCap className="w-5 h-5 inline mr-2" />
              National Diploma (ND)
            </button>
            <button
              type="button"
              onClick={() => setActiveProgram('hnd')}
              className={`px-6 py-3 text-sm font-medium rounded-r-lg ${
                activeProgram === 'hnd' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="w-5 h-5 inline mr-2" />
              Higher National Diploma (HND)
            </button>
          </div>
        </div>

        {/* School Filters - Only for ND */}
        {activeProgram === 'nd' && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {schoolFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveSchool(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  activeSchool === filter.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        )}

        {/* Program Listings */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            {activeProgram === 'nd' ? 'National Diploma Programs' : 'Higher National Diploma Programs'}
          </h2>
          
          <div className="space-y-6">
            {(activeProgram === 'nd' ? ndPrograms[activeSchool] : hndPrograms.all).map((school, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold text-blue-800 mb-4">{school.school}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {school.programs.map((program, idx) => (
                    <div key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{program}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements and Application Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Requirements */}
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              <FileText className="w-6 h-6 inline mr-2 text-blue-600" />
              Entry Requirements
            </h2>
            <div className="space-y-4">
              {activeProgram === 'nd' ? (
                <>
                  <p className="text-gray-700">Minimum of <strong>Five (5) Credit Passes</strong> in SSCE, NECO, WAEC, GCE, NBC or NABTEB at not more than two sittings.</p>
                  <p className="text-gray-700">Minimum UTME score of <strong>100 points</strong>.</p>
                  <p className="text-gray-700">Candidates must have chosen Federal Polytechnic, Bida as their first choice.</p>
                  <p className="text-gray-700 font-semibold text-blue-700">Candidates must ensure their O'Level results are uploaded on the JAMB CAPS portal.</p>
                </>
              ) : (
                <>
                  <p className="text-gray-700"><strong>National Diploma (ND)</strong> in relevant program with minimum of <strong>Lower Credit</strong>.</p>
                  <p className="text-gray-700">Minimum of <strong>one year</strong> Post-ND cognate working experience (Industrial Training).</p>
                  <p className="text-gray-700">Minimum of <strong>Five (5) Credit Passes</strong> in relevant O'Level subjects.</p>
                  <p className="text-gray-700 font-semibold text-blue-700">Applicants from other institutions must send official transcripts to the Registrar.</p>
                </>
              )}
            </div>
          </div>

          {/* Application Process */}
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              <Calendar className="w-6 h-6 inline mr-2 text-blue-600" />
              Application Process
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                <strong>Application Fee:</strong> {activeProgram === 'nd' ? '₦2,000.00 & Evening DCE ₦15,000' : '₦15,000.00 & Evening DCE ₦15,000'}
              </p>
              <p className="text-gray-700">
                <strong>Application Deadline:</strong> October 31, 2026
              </p>
              <p className="text-gray-700">
                Apply online at: <a href="https://portal.fedpolybida.edu.ng/" className="text-blue-600 hover:underline">https://portal.fedpolybida.edu.ng/</a>
              </p>
              <p className="text-gray-700 font-semibold">
                Important: No change, rearrangement or correction of names will be entertained once admitted.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-blue-50 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Assistance?</h2>
          <p className="text-gray-700 mb-6">
            Contact our admission office for any inquiries or support with your application.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <div>
              <a href="mailto:admissionofficer@fedpolybida.edu.ng" className="text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                admissionofficer@fedpolybida.edu.ng
              </a>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:08167470279" className="text-blue-600 hover:text-blue-800 font-medium">
                08167470279
              </a>
              <span className="hidden sm:block text-gray-400">•</span>
              <a href="tel:08054208454" className="text-blue-600 hover:text-blue-800 font-medium">
                08054208454
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionsPage;